import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';

// pdfMake fontlarını kaydet
if (pdfFonts && pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) {
    pdfMake.vfs = pdfFonts.pdfMake.vfs;
} else if (pdfFonts) {
    pdfMake.vfs = pdfFonts;
}

/**
 * Garanti Denetim Formu PDF (pdfMake ile) - dikey (A4 portrait) format.
 * Türkçe karakter desteği tam olarak çalışır.
 *
 * data: WarrantyAuditPdfDataDto (bkz. backend sözleşmesi)
 */
class WarrantyAuditPdfFormPdfMake {
    label(translations, key, fallback) {
        const v = translations?.[key];
        return v && v !== 'NOTRANSLATION' ? v : fallback;
    }

    formatDate(dateValue) {
        if (!dateValue) return '-';
        try {
            return new Date(dateValue).toLocaleDateString('tr-TR');
        } catch {
            return String(dateValue);
        }
    }

    resolveScoreBand(data, score, fallbackText, fallbackColor) {
        const parseScore = (value) => {
            if (value == null || value === '') return NaN;
            return Number(String(value).replace(',', '.'));
        };
        const numericScore = parseScore(score);
        const bands = Array.isArray(data?.scoreBands) ? data.scoreBands : [];

        if (Number.isFinite(numericScore)) {
            const normalizedBands = bands
                .map((band) => ({
                    band,
                    minScore: parseScore(band.minScore ?? band.min ?? band.minPoint),
                    maxScore: parseScore(band.maxScore ?? band.max ?? band.maxPoint)
                }))
                .filter((band) => Number.isFinite(band.minScore) && Number.isFinite(band.maxScore))
                .sort((a, b) => a.minScore - b.minScore);

            const bandMatch = normalizedBands.find((currentBand, index) => {
                const nextBand = normalizedBands[index + 1];
                const upperLimit = nextBand?.minScore === currentBand.maxScore + 1 ? nextBand.minScore : currentBand.maxScore;
                const isBelowUpperLimit = nextBand?.minScore === currentBand.maxScore + 1
                    ? numericScore < upperLimit
                    : numericScore <= upperLimit;

                return numericScore >= currentBand.minScore && isBelowUpperLimit;
            });
            const band = bandMatch?.band;
            if (band) {
                return {
                    text: band.text || band.code || '-',
                    color: band.color || '#64748b'
                };
            }
        }

        return {
            text: fallbackText || '-',
            color: fallbackColor || '#64748b'
        };
    }

    formatScore(score, fractionDigits = 2) {
        const numericScore = Number(String(score ?? 0).replace(',', '.'));
        if (!Number.isFinite(numericScore)) return String(score ?? '-');
        return numericScore.toFixed(fractionDigits);
    }

    generate(data = {}, translations = {}) {
        const t = translations;

        const docDefinition = {
            pageSize: 'A4',
            pageOrientation: 'portrait',
            pageMargins: [30, 30, 30, 30],
            content: [
                this.createHeader(data, t),
                { text: '', margin: [0, 6] },
                this.createInfoTable(data, t),
                { text: '', margin: [0, 10] },
                this.createCriteriaTable(data, t),
                // Toplam Puan ve Puan Aralıkları sonraki sayfaya taşınabilir
                { text: '', margin: [0, 12], pageBreak: 'after' },
                this.createSummary(data, t),
                { text: '', margin: [0, 10] },
                this.createScoreBandsLegend(data, t)
            ],
            styles: {
                tableHeader: { bold: true, fontSize: 8, color: '#000', fillColor: '#e9eef5' },
                cell: { fontSize: 8 }
            },
            defaultStyle: { font: 'Roboto', fontSize: 8 }
        };

        return pdfMake.createPdf(docDefinition);
    }

    createHeader(data, t) {
        return {
            columns: [
                {
                    width: 'auto',
                    stack: [
                        { svg: this.getKarsanLogoSvg(), width: 90 },
                        { text: 'KARSAN OTOMOTİV', fontSize: 9, bold: true, margin: [0, 4, 0, 0] }
                    ]
                },
                {
                    width: '*',
                    text: this.label(t, 'title', 'GARANTİ DENETİM FORMU'),
                    fontSize: 14,
                    bold: true,
                    alignment: 'right',
                    margin: [0, 10, 0, 0]
                }
            ]
        };
    }

    createInfoTable(data, t) {
        const row = (label, value) => [
            { text: `${label}:`, bold: true, fontSize: 9, margin: [2, 2, 2, 2] },
            { text: value != null && value !== '' ? String(value) : '-', fontSize: 9, margin: [2, 2, 2, 2] }
        ];

        return {
            table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [
                    [...row(this.label(t, 'auditNo', 'Denetim Nr'), data.auditNo), ...row(this.label(t, 'auditDate', 'Denetim Tarihi'), this.formatDate(data.auditDate))],
                    [...row(this.label(t, 'dealer', 'Bayi/Servis'), data.dealerName || `${data.bosBranchCode || ''}`), ...row(this.label(t, 'auditor', 'Denetçi'), data.auditorName)]
                ]
            },
            layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => '#888',
                vLineColor: () => '#888'
            }
        };
    }

    createCriteriaTable(data, t) {
        // pdfMake hücre içi dikey hizalamayı desteklemediği için, birleştirilmiş (rowSpan)
        // hücrelerin metnini dikey ortaya yaklaştırmak adına satır yüksekliklerini tahmin edip
        // üstten boşluk (margin) veriyoruz. Tahminler taşmayı önlemek için temkinli tutulur.
        const LINE_H = 9.5; // 8pt yazıda yaklaşık satır yüksekliği
        const CELL_VPAD = 4; // hücre dikey iç boşluğu (üst+alt)
        const SAFETY = 0.9; // taşma riskine karşı güvenlik katsayısı
        // Kolon genişliklerine göre yaklaşık satır başına karakter (fazla = az satır = güvenli)
        const CPL = { criterion: 16, subCriterion: 24, criterionText: 30, evaluationText: 20 };

        const estLines = (text, cpl) => Math.max(1, Math.ceil(String(text ?? '').length / cpl));
        const evalRowHeight = (evaluation) =>
            Math.max(
                estLines(evaluation?.criterionText, CPL.criterionText),
                estLines(evaluation?.evaluationText, CPL.evaluationText)
            ) * LINE_H + CELL_VPAD;
        const centerMargin = (totalHeight, contentHeight) => Math.max(0, ((totalHeight - contentHeight) / 2) * SAFETY);

        const body = [
            [
                { text: this.label(t, 'criterion', 'Kriter'), style: 'tableHeader' },
                { text: this.label(t, 'sort', 'Sıralama'), style: 'tableHeader', alignment: 'center' },
                { text: this.label(t, 'subCriterion', 'Alt Kriter'), style: 'tableHeader' },
                { text: this.label(t, 'criterionText', 'Değerlendirme Kriter'), style: 'tableHeader' },
                { text: this.label(t, 'evaluationText', 'Değerlendirme'), style: 'tableHeader' },
                { text: this.label(t, 'point', 'Puan'), style: 'tableHeader', alignment: 'center' },
                { text: this.label(t, 'score', 'Denetim'), style: 'tableHeader', alignment: 'center' }
            ]
        ];

        (data.criteria || []).forEach((criterion) => {
            const subs = criterion.subCriteria || [];
            const criterionRowSpan = subs.reduce((acc, s) => acc + Math.max(1, (s.evaluations || []).length), 0) || 1;
            let criterionPlaced = false;

            if (subs.length === 0) {
                body.push([
                    { text: criterion.name, style: 'cell', bold: true },
                    { text: '', style: 'cell' },
                    { text: '', style: 'cell' },
                    { text: '', style: 'cell' },
                    { text: '', style: 'cell' },
                    { text: '', style: 'cell' },
                    { text: '', style: 'cell' }
                ]);
                return;
            }

            // Kriter bloğunun toplam (tahmini) yüksekliği → kriter adını dikey ortalamak için
            const criterionTotalHeight = subs.reduce((acc, s) => {
                const evals = (s.evaluations || []).length > 0 ? s.evaluations : [null];
                return acc + evals.reduce((a, ev) => a + evalRowHeight(ev), 0);
            }, 0);
            const criterionTopMargin = centerMargin(criterionTotalHeight, estLines(criterion.name, CPL.criterion) * LINE_H);

            subs.forEach((sub) => {
                const evals = (sub.evaluations || []).length > 0 ? sub.evaluations : [null];
                const subRowSpan = evals.length;

                // Alt kriter bloğunun toplam (tahmini) yüksekliği → alt kriter/sıralama/denetim hücreleri için
                const subTotalHeight = evals.reduce((a, ev) => a + evalRowHeight(ev), 0);
                const subNameTopMargin = centerMargin(subTotalHeight, estLines(sub.name, CPL.subCriterion) * LINE_H);
                const subSingleTopMargin = centerMargin(subTotalHeight, LINE_H);

                evals.forEach((evaluation, idx) => {
                    const row = [];

                    // Kriter
                    if (!criterionPlaced) {
                        row.push({ text: criterion.name, style: 'cell', bold: true, rowSpan: criterionRowSpan, margin: [0, criterionTopMargin, 0, 0] });
                        criterionPlaced = true;
                    } else {
                        row.push({});
                    }

                    // Sıralama + Alt Kriter + Denetim (alt kriter başına tek)
                    if (idx === 0) {
                        row.push({ text: sub.displayCode || String(sub.sort ?? ''), style: 'cell', alignment: 'center', rowSpan: subRowSpan, margin: [0, subSingleTopMargin, 0, 0] });
                        row.push({ text: sub.name, style: 'cell', rowSpan: subRowSpan, margin: [0, subNameTopMargin, 0, 0] });
                    } else {
                        row.push({});
                        row.push({});
                    }

                    row.push({ text: evaluation?.criterionText || '', style: 'cell' });
                    row.push({ text: evaluation?.evaluationText || '', style: 'cell' });
                    row.push({ text: evaluation?.point != null ? String(evaluation.point) : '', style: 'cell', alignment: 'center' });

                    if (idx === 0) {
                        const scoreText = sub.maxScore != null ? `${sub.score ?? 0} / ${sub.maxScore}` : `${sub.score ?? 0}`;
                        row.push({ text: scoreText, style: 'cell', alignment: 'center', bold: true, rowSpan: subRowSpan, margin: [0, subSingleTopMargin, 0, 0] });
                    } else {
                        row.push({});
                    }

                    body.push(row);
                });
            });
        });

        return {
            table: {
                headerRows: 1,
                widths: ['16%', '8%', '20%', '24%', '16%', '6%', '10%'],
                body
            },
            layout: {
                hLineWidth: () => 0.4,
                vLineWidth: () => 0.4,
                hLineColor: () => '#999',
                vLineColor: () => '#999'
            }
        };
    }

    createSummary(data, t) {
        const averageScore = data.avarageScore ?? data.averageScore ?? 0;
        const averageBand = this.resolveScoreBand(
            data,
            averageScore,
            null,
            null
        );

        return {
            columns: [
                {
                    width: 'auto',
                    table: {
                        widths: [125, 90],
                        body: [
                            [
                                { text: this.label(t, 'totalScore', 'Toplam Puan'), bold: true, fontSize: 11, margin: [4, 4, 8, 4] },
                                { text: String(data.totalScore ?? 0), fontSize: 14, bold: true, margin: [8, 4, 10, 4] }
                            ],
                            [
                                { text: this.label(t, 'result', 'Sonuç'), bold: true, fontSize: 11, margin: [4, 4, 8, 4] },
                                {
                                    text: data.scoreBandText || '-',
                                    fontSize: 11,
                                    bold: true,
                                    color: '#ffffff',
                                    fillColor: data.scoreBandColor || '#64748b',
                                    margin: [4, 4, 4, 4]
                                }
                            ]
                        ]
                    },
                    layout: this.summaryTableLayout()
                },
                { width: 18, text: '' },
                {
                    width: 'auto',
                    table: {
                        widths: [125, 90],
                        body: [
                            [
                                { text: this.label(t, 'averageScore', 'Ortalama Puan'), bold: true, fontSize: 11, margin: [4, 4, 8, 4] },
                                { text: this.formatScore(averageScore), fontSize: 14, bold: true, margin: [8, 4, 10, 4] }
                            ],
                            [
                                { text: this.label(t, 'result', 'Sonuç'), bold: true, fontSize: 11, margin: [4, 4, 8, 4] },
                                {
                                    text: averageBand.text,
                                    fontSize: 11,
                                    bold: true,
                                    color: '#ffffff',
                                    fillColor: averageBand.color,
                                    margin: [4, 4, 4, 4]
                                }
                            ]
                        ]
                    },
                    layout: this.summaryTableLayout()
                }
            ],
            columnGap: 0
        };
    }

    summaryTableLayout() {
        return {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#888',
            vLineColor: () => '#888'
        };
    }

    createScoreBandsLegend(data, t) {
        const bands = data.scoreBands || [];
        if (bands.length === 0) return { text: '' };

        const body = [
            [
                { text: this.label(t, 'result', 'Sonuç'), style: 'tableHeader' },
                { text: 'Min', style: 'tableHeader', alignment: 'center' },
                { text: 'Max', style: 'tableHeader', alignment: 'center' }
            ]
        ];

        bands.forEach((band) => {
            body.push([
                { text: band.text || band.code, style: 'cell', fillColor: band.color || '#ffffff', color: '#ffffff' },
                { text: String(band.minScore), style: 'cell', alignment: 'center' },
                { text: String(band.maxScore), style: 'cell', alignment: 'center' }
            ]);
        });

        return {
            stack: [
                { text: this.label(t, 'scoreBands', 'Puan Aralıkları'), bold: true, fontSize: 10, margin: [0, 0, 0, 4] },
                {
                    table: { widths: ['*', '20%', '20%'], body },
                    layout: {
                        hLineWidth: () => 0.4,
                        vLineWidth: () => 0.4,
                        hLineColor: () => '#999',
                        vLineColor: () => '#999'
                    }
                }
            ]
        };
    }

    getKarsanLogoSvg() {
        return `<svg viewBox="0 0 66 17.375" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs><style>.st1{fill:#f05133}</style></defs>
            <path class="st1" d="M0.473 8.597 8.599 0.016H0v17.157h8.582z" />
            <path class="st1" d="M17.147 0v17.189l-8.559 -8.593z" />
        </svg>`;
    }
}

export default WarrantyAuditPdfFormPdfMake;
